import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, Loader2, Building2, Upload, ImageOff } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

export const BrandingModal = ({ onClose }) => {
  const { user, refreshUser } = useApp();
  const b = user?.branding || {};
  const [f, setF] = useState({
    company_name: b.company_name || "",
    logo: b.logo || "",
    display_name: b.display_name || user?.name || "",
    title: b.title || "",
  });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1_200_000) { toast.error("Logo too large — please use an image under 1MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setF((p) => ({ ...p, logo: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/branding", f);
      await refreshUser();
      toast.success("Branding saved — it will appear on your exports.");
      onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] grid place-items-center p-4 bg-black/60" onClick={onClose}>
      <motion.div initial={{ scale: 0.96, y: 10 }} animate={{ scale: 1, y: 0 }} className="rud-card rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="branding-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-serif text-2xl flex items-center gap-2"><Building2 size={18} className="text-brand" /> Your brand on exports</h3>
          <button onClick={onClose} className="text-sec hover:text-[var(--rud-text)]"><X size={18} /></button>
        </div>
        <p className="text-xs text-sec mb-4">Add your company logo, name and title. My Kind stamps these on every PDF, Word and PowerPoint you export.</p>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 rounded-lg bg-elevated border border-rud grid place-items-center overflow-hidden shrink-0">
              {f.logo ? <img src={f.logo} alt="logo" className="w-full h-full object-contain" data-testid="branding-logo-preview" /> : <ImageOff size={18} className="text-sec" />}
            </div>
            <div className="flex-1">
              <input ref={fileRef} type="file" accept="image/*" onChange={onLogo} className="hidden" data-testid="branding-logo-file" />
              <button onClick={() => fileRef.current?.click()} className="btn-ghost rounded-md h-9 px-3 text-sm flex items-center gap-2"><Upload size={14} /> Upload logo</button>
              <input value={f.logo?.startsWith("data:") ? "" : f.logo} onChange={set("logo")} placeholder="…or paste a logo URL" className="rud-input w-full rounded-md px-3 py-2 text-xs mt-2" data-testid="branding-logo-url" />
            </div>
          </div>
          <input data-testid="branding-company" value={f.company_name} onChange={set("company_name")} placeholder="Company name" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="branding-name" value={f.display_name} onChange={set("display_name")} placeholder="Your name" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
            <input data-testid="branding-title" value={f.title} onChange={set("title")} placeholder="Title (e.g. CEO)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
          </div>
          <button data-testid="branding-save" onClick={save} disabled={busy} className="btn-primary w-full h-11 flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Building2 size={16} />} Save branding
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};
